/// <amd-module name="Artifacts/FeedSettings/containers/FeedSettingsHubContainer" />

import { FeedSettingsHub } from "Artifacts/FeedSettings/components/FeedSettingsHub";
import {
  FeedSettingsActions,
  FeedSettingsModule,
  IFeedSettingsModuleState,
} from "Artifacts/FeedSettings/redux";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { GenericModalId, IGenericModalProps } from "Artifacts/Root/components/GenericModal";
import { ComponentState } from "Artifacts/Root/Constants";
import { getSelectedView } from "Artifacts/Root/helpers/FeedHelpers";
import { useFeed } from "Artifacts/Root/hooks/FeedUrlResolver";
import { connectDynamicModules } from "Artifacts/Root/redux";
import { RootActions } from "Artifacts/Root/redux/Actions";
import { ICurrentModal } from "Artifacts/Root/redux/Contracts";
import { feedHubRoute } from "Artifacts/Root/routing/Constants";
import { feedSettingsContainerName } from "Artifacts/Root/routing/Definitions";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";
import * as Utils_String from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, useComponentContext } from "VSS/Platform/Layout";

interface IFeedSettingsHubMapStateToProps {
  componentState: ComponentState;
  deleteFeedComponentState: ComponentState;
  settings?: IFeedSettings;
  permanentDeleteFeedComponentState: ComponentState;
  isUpstreamsDirty: boolean;
}

type IFeedSettingsHubContainerProps = IFeedSettingsHubMapStateToProps & typeof Actions;

const DeleteFeedModalName = "DeleteFeedModal";
const PermanantDeleteFeedModalName = "PermanantDeleteFeedModal";

const Actions = {
  initLoad: FeedSettingsActions.initLoad,
  deleteFeed: FeedSettingsActions.deleteFeed,
  restoreFeed: FeedSettingsActions.restoreFeed,
  permanentDeleteFeed: FeedSettingsActions.permanentDeleteFeed,
  showModal: RootActions.showModal,
  hideModal: RootActions.hideModal,
};

const FeedSettingsHubContainer: React.FC<IFeedSettingsHubContainerProps> = ({
  componentState,
  deleteFeedComponentState,
  settings,
  initLoad,
  deleteFeed,
  restoreFeed,
  permanentDeleteFeed,
  showModal,
  hideModal,
  permanentDeleteFeedComponentState,
  isUpstreamsDirty,
}) => {
  const { feed, resolution } = useFeed();
  const view = getSelectedView(feed);
  const history = useHistory();
  const [currentFeedId, setCurrentFeedId] = useState<string | undefined>();

  React.useEffect(() => {
    if (feed && feed.id !== currentFeedId) {
      initLoad(feed);
      setCurrentFeedId(feed.id);
    }
  }, [initLoad, feed, currentFeedId]);

  const { pageContext } = useComponentContext();
  const isUpstreamForPublicFeedsAllowed =
    isFeatureFlagEnabled(pageContext, "Packaging.Feed.UpstreamsAllowedForPublicFeeds", false) ||
    isFeatureFlagEnabled(pageContext, "Packaging.Feed.UpstreamsAllowedForPublicFeeds.MSFT", false);

  const showDeleteFeedModal = React.useCallback(() => {
    const onDeleted = () => {
      hideModal();
      history.push(generatePath(feedHubRoute, { feedIdentifier: feed?.name }));
      window.location.reload();
    };

    const modalProps: ICurrentModal<IGenericModalProps> = {
      modalId: GenericModalId,
      props: {
        title: Utils_String.format(Res.FeedDeletionDialogContent_Title, feed?.name),
        primaryButtonText: Res.Delete_Label,
        primaryButtonDanger: true,
        body: Res.FeedDeletionDialogContent_DeleteFeedText,
        warningCardContent: Res.FeedDeletionDialogContent_SevereWarningText,
        onPrimaryClicked: () => feed && deleteFeed(feed, onDeleted),
        onDismiss: hideModal,
        primaryButtonComponentState: deleteFeedComponentState,
      },
    };
    showModal(modalProps, DeleteFeedModalName);
  }, [deleteFeed, deleteFeedComponentState, feed, hideModal, history, showModal]);

  const showPermanentDeleteFeedModal = React.useCallback(() => {
    const onDeleted = () => {
      hideModal();
      // Redirect to extension root and allow server to re-route us to best feed
      // or zero feeds experience if appropriate
      history.push(generatePath("/"));
      window.location.reload();
    };

    const modalProps: ICurrentModal<IGenericModalProps> = {
      modalId: GenericModalId,
      props: {
        title: Res.FeedPermanentDeletionDialogContent_Title,
        primaryButtonText: Res.Delete_Label,
        primaryButtonDanger: true,
        body: Res.FeedPermanentDeletionDialogContent_PermanentDeleteFeedText,
        warningCardContent: Res.FeedPermanentDeletionDialogContent_SevereWarningText,
        onPrimaryClicked: () => feed && permanentDeleteFeed(feed, onDeleted),
        onDismiss: hideModal,
        primaryButtonComponentState: permanentDeleteFeedComponentState,
      },
    };
    showModal(modalProps, PermanantDeleteFeedModalName);
  }, [feed, hideModal, history, permanentDeleteFeed, permanentDeleteFeedComponentState, showModal]);

  return (
    <DefaultLoader state={resolution}>
      <FeedSettingsHub
        feed={feed}
        componentState={componentState}
        view={view}
        settings={settings}
        deleteFeed={showDeleteFeedModal}
        restoreFeed={() => {
          feed &&
            restoreFeed(feed, () => {
              history.push(generatePath(feedHubRoute, { feedIdentifier: feed.name }));
              window.location.reload();
            });
        }}
        permanentDeleteFeed={showPermanentDeleteFeedModal}
        isUpstreamsDirty={isUpstreamsDirty}
        isUpstreamForPublicFeedsAllowed={isUpstreamForPublicFeedsAllowed}
      />
    </DefaultLoader>
  );
};

function mapStateToProps(state: IFeedSettingsModuleState): IFeedSettingsHubMapStateToProps {
  const hubState = state["feed-settings"].hubState;
  return {
    componentState: hubState.componentState,
    settings: hubState.settings,
    deleteFeedComponentState: hubState.deleteFeedComponentState,
    permanentDeleteFeedComponentState: hubState.permanentDeleteFeedComponentState,
    isUpstreamsDirty: hubState.isUpstreamsDirty,
  };
}

const ConnectedFeedSettingsHubContainer = connectDynamicModules(
  connect(mapStateToProps, Actions)(FeedSettingsHubContainer),
  FeedSettingsModule,
);

Components.add(feedSettingsContainerName, ConnectedFeedSettingsHubContainer);

export {
  ConnectedFeedSettingsHubContainer as FeedSettingsHubContainer,
  IFeedSettingsHubContainerProps,
};
